import { useEffect, useState, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"

interface OnboardingSpotlightProps {
  targetSelector: string
  active: boolean
  onClick?: () => void
}

const PADDING = 8

/** Returns the offset of the explorer root relative to the viewport */
const getContainerOffset = () => {
  const root = document.getElementById("explore-map-v2-root")
  if (!root) return { x: 0, y: 0 }
  const r = root.getBoundingClientRect()
  return { x: r.x, y: r.y }
}

export const OnboardingSpotlight = ({ targetSelector, active, onClick }: OnboardingSpotlightProps) => {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const rafRef = useRef(0)

  const updateRect = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const el = document.querySelector(targetSelector)
      if (el) setRect(el.getBoundingClientRect())
    })
  }, [targetSelector])

  useEffect(() => {
    if (!active) return
    setRect(null)

    const resizeObserver = new ResizeObserver(updateRect)
    let mutationObserver: MutationObserver | null = null

    const attach = (el: Element) => {
      mutationObserver?.disconnect()
      resizeObserver.observe(el)
      updateRect()
    }

    const el = document.querySelector(targetSelector)
    if (el) {
      attach(el)
    } else {
      // Element not in DOM yet — watch for it to appear
      mutationObserver = new MutationObserver(() => {
        const found = document.querySelector(targetSelector)
        if (found) attach(found)
      })
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }

    window.addEventListener("resize", updateRect)
    window.addEventListener("scroll", updateRect, true)

    return () => {
      cancelAnimationFrame(rafRef.current)
      resizeObserver.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener("resize", updateRect)
      window.removeEventListener("scroll", updateRect, true)
    }
  }, [active, targetSelector, updateRect])

  if (!active || !rect) return null

  // Convert viewport-relative rect to container-relative coords
  const offset = getContainerOffset()
  const x = rect.x - offset.x - PADDING
  const y = rect.y - offset.y - PADDING
  const w = rect.width + PADDING * 2
  const h = rect.height + PADDING * 2
  const r = 12

  return (
    <AnimatePresence>
      <motion.div
        key="spotlight"
        className="absolute inset-0 z-[45]"
        onClick={onClick}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        style={{ pointerEvents: "auto" }}
      >
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <motion.rect
                fill="black"
                rx={r}
                ry={r}
                animate={{ x, y, width: w, height: h }}
                transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.45)" mask="url(#spotlight-mask)" />
        </svg>
      </motion.div>
    </AnimatePresence>
  )
}

export const useTargetRect = (selector: string, active: boolean) => {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const rafRef = useRef(0)

  const updateRect = useCallback(() => {
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const el = document.querySelector(selector)
      if (el) setRect(el.getBoundingClientRect())
    })
  }, [selector])

  useEffect(() => {
    if (!active) return
    setRect(null)

    const resizeObserver = new ResizeObserver(updateRect)
    let mutationObserver: MutationObserver | null = null

    const attach = (el: Element) => {
      mutationObserver?.disconnect()
      resizeObserver.observe(el)
      updateRect()
    }

    const el = document.querySelector(selector)
    if (el) {
      attach(el)
    } else {
      mutationObserver = new MutationObserver(() => {
        const found = document.querySelector(selector)
        if (found) attach(found)
      })
      mutationObserver.observe(document.body, { childList: true, subtree: true })
    }

    window.addEventListener("resize", updateRect)
    window.addEventListener("scroll", updateRect, true)

    return () => {
      cancelAnimationFrame(rafRef.current)
      resizeObserver.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener("resize", updateRect)
      window.removeEventListener("scroll", updateRect, true)
    }
  }, [active, selector, updateRect])

  return rect
}
